import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface QueueItem {
  masterBriefId: string;
  briefSlug: string;
  voiceId?: string | null;
  title: string;
  artist: string;
  artwork: string | null;
  audioUrl: string | null;
  audioDurationSeconds: number | null;
}

interface BriefQueueContextType {
  queue: QueueItem[];
  currentIndex: number;
  getNextTrack: () => QueueItem | null;
  getPreviousTrack: () => QueueItem | null;
  setCurrentIndex: (index: number) => void;
  findIndexByMasterBriefId: (masterBriefId: string, voiceId?: string | null) => number;
  refreshQueue: () => Promise<void>;
  isLoading: boolean;
}

const BriefQueueContext = createContext<BriefQueueContextType | null>(null);

export function useBriefQueue() {
  const context = useContext(BriefQueueContext);
  if (!context) {
    throw new Error('useBriefQueue must be used within BriefQueueProvider');
  }
  return context;
}

export function useBriefQueueOptional() {
  return useContext(BriefQueueContext);
}

export function BriefQueueProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const stableUserIdRef = useRef<string | null>(null);
  const currentMasterBriefIdRef = useRef<string | null>(null);

  const refreshQueue = useCallback(async () => {
    if (authLoading) return;
    
    if (!user?.id) {
      setQueue([]);
      stableUserIdRef.current = null;
      return;
    }
    
    stableUserIdRef.current = user.id;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_briefs')
        .select(`
          id,
          slug,
          voice_id,
          audio_status,
          master_brief_id,
          master_briefs (
            id,
            episode_name,
            podcast_name,
            episode_thumbnail,
            pipeline_status,
            brief_audio_files (voice_id, status, audio_url, audio_duration_seconds)
          )
        `)
        .eq('user_id', user.id)
        .eq('is_hidden', false)
        .eq('is_completed', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[BriefQueue] Error fetching queue:', error);
        return;
      }

      const queueItems: QueueItem[] = (data || [])
        .map((ub: any) => {
          const voiceAudio = (ub.master_briefs?.brief_audio_files || [])
            .find((file: any) => file.voice_id === ub.voice_id && file.status === 'completed' && file.audio_url);

          return {
          masterBriefId: ub.master_brief_id,
          briefSlug: ub.slug,
          voiceId: ub.voice_id || null,
          title: ub.master_briefs?.episode_name || 'Untitled',
          artist: ub.master_briefs?.podcast_name || 'Unknown',
          artwork: ub.master_briefs?.episode_thumbnail || null,
          audioUrl: voiceAudio?.audio_url || null,
          audioDurationSeconds: voiceAudio?.audio_duration_seconds || null,
          };
        })
        .filter((item: QueueItem) => !!item.audioUrl);

      setQueue(queueItems);

      // Re-anchor currentIndex to the same masterBriefId after queue mutation
      if (currentMasterBriefIdRef.current) {
        const newIdx = queueItems.findIndex(item => item.masterBriefId === currentMasterBriefIdRef.current);
        if (newIdx >= 0) {
          setCurrentIndex(newIdx);
        }
        // If not found (completed/hidden), leave index as-is
        // so getNextTrack() returns the item that slid into this position
      }
    } catch (err) {
      console.error('[BriefQueue] Failed to refresh queue:', err);
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, user?.id]);

  useEffect(() => {
    if (!authLoading) {
      refreshQueue();
    }
  }, [authLoading, refreshQueue]);

  const findIndexByMasterBriefId = useCallback((masterBriefId: string, voiceId?: string | null): number => {
    return queue.findIndex(item => item.masterBriefId === masterBriefId && (voiceId === undefined || (item.voiceId || null) === (voiceId || null)));
  }, [queue]);

  const setCurrentIndexAndAnchor = useCallback((index: number) => {
    setCurrentIndex(index);
    setQueue(prev => {
      currentMasterBriefIdRef.current = prev[index]?.masterBriefId || null;
      return prev; // no mutation
    });
  }, []);

  const getNextTrack = useCallback((): QueueItem | null => {
    if (queue.length === 0) return null;
    if (currentIndex < 0) return queue[0] || null;
    const nextIndex = currentIndex + 1;
    if (nextIndex < queue.length) return queue[nextIndex];
    return null;
  }, [queue, currentIndex]);

  const getPreviousTrack = useCallback((): QueueItem | null => {
    if (queue.length === 0 || currentIndex <= 0) return null;
    return queue[currentIndex - 1] || null;
  }, [queue, currentIndex]);

  const value: BriefQueueContextType = {
    queue,
    currentIndex,
    getNextTrack,
    getPreviousTrack,
    setCurrentIndex: setCurrentIndexAndAnchor,
    findIndexByMasterBriefId,
    refreshQueue,
    isLoading,
  };

  return (
    <BriefQueueContext.Provider value={value}>
      {children}
    </BriefQueueContext.Provider>
  );
}
